//can consolidate

import React from 'react'
import { StyleSheet, View } from 'react-native'

import { DS } from '@/design-system'

import { formatChipsNumber } from '@/components/GameAssets/Player/PlayerBox'
import { formatMs } from '@/utils/timeFormatter'

import * as cth from '@/router/(root)/cth/cthModule'
import * as d10 from '@/router/(root)/daily10/d10Module'
import * as pocketPick from '@/router/(root)/pocketPick/pocketPickModule'

import { defaultTheme } from '@/components/Mini/theme'

function PocketPickStatsCard() {
  const { status } = pocketPick.usePocketPickQueryStatus()
  const theme = pocketPick.useStore(s => s.theme)

  return <StatsCard status={status} T={theme} />
}

function CthStatsCard() {
  const { status } = cth.useCthQueryStatus()
  const theme = cth.useStore(s => s.theme)

  return <StatsCard status={status} T={theme} />
}

function D10StatsCard() {
  const { status } = d10.useD10QueryStatus()
  const theme = d10.useStore(s => s.theme)

  return <Daily10StatsCard status={status} T={theme} />
}

type status = { status: string; [key: string]: any } | null | undefined





function Daily10StatsCard({ status, T=defaultTheme }: { status: status; T?: typeof defaultTheme }) {

  if (status?.status !== 'completed') return null

  const { result } = status
  const percentage = Math.round((result.finalChips / result.maxPossibleChips) * 100)
  const preflopFoldsUsed = result.handOutcomes.filter((o: string) => o === 'free_fold').length

  const DEFAULT_FREE_PREFLOP_FOLDS = d10.useStore(s => s.DEFAULT_FREE_PREFLOP_FOLDS)

  const cardBackgroundColor = T.surface
  const cardBorderColor = T.surfaceBorder
  const titleTextColor = T.textDefault
  const subtitleTextColor = T.textDefault

  const chipsTextColor = T.isDark ? T.accent : T.textDefault
  const labelTextColor = T.isDark ? T.textDefaultDim : T.textDefault
  const progressBarColor = T.isDark ? T.textDefaultDim : T.surfaceBorder
  const progressBarFillColor = T.accent

  let titleText: string
  let showSubtitleText = false

  titleText = "Your result today"

  return (
    <View style={[styles.card, { backgroundColor: cardBackgroundColor, borderColor: cardBorderColor }]}>
      {/*card row 1*/}
      <View style={styles.titleContainer}>
        <DS.Text style={[styles.titleContainerText, styles.titleText, { color: titleTextColor }]} variant="Heading 3/Medium">
          {titleText}
        </DS.Text>
        {showSubtitleText && (
          <DS.Text variant="Body Medium/Regular" style={{ color: subtitleTextColor }}>
            Keep practicing to improve your hand reading.
          </DS.Text>
        )}
      </View>

      {/*card row 2*/}
      <View style={styles.statsRow}>

        <View style={styles.statBox}>
          <DS.Text variant="Heading 2/Medium" style={{color: chipsTextColor}}>
            {formatChipsNumber(result.finalChips)}
          </DS.Text>
          <DS.Text style={{color: labelTextColor}} variant="Body Small/Regular" >
            out of {formatChipsNumber(result.maxPossibleChips)} chips ({percentage}%)
          </DS.Text>
        </View>
  
      </View>

      {/*card row 3*/}
      <View>

        <View style={[styles.progressBar, {backgroundColor: progressBarColor}]}>
          <View style={[styles.progressFill, { width: `${percentage}%`, backgroundColor: progressBarFillColor }]} />
        </View>
        
      </View>

      {/*card row 4*/}
      <View style={{gap:4}}>
        <View style={[styles.statsRow, {justifyContent: "space-between"}]}>
          <DS.Text style={[styles.titleContainerText, {color: labelTextColor}]} variant="Body Small/Regular" >
            Hands Played
          </DS.Text>
          <DS.Text style={[styles.titleContainerText, {color: labelTextColor}]} variant="Body Small/Regular" >
            {result.countedHands}
          </DS.Text>
        </View>
        <View style={[styles.statsRow, {justifyContent: "space-between"}]}>
          <DS.Text style={[styles.titleContainerText, {color: labelTextColor}]} variant="Body Small/Regular" >
            Pre-flop Folds Used
          </DS.Text>
          <DS.Text style={[styles.titleContainerText, {color: labelTextColor}]} variant="Body Small/Regular" >
            {preflopFoldsUsed}/{DEFAULT_FREE_PREFLOP_FOLDS}
          </DS.Text>
        </View>
      </View>
    </View>
  )
}

function StatsCard({ status, T=defaultTheme }: { status: status; T?: typeof defaultTheme }) {
  if (status?.status !== 'completed') return null

  const cardBackgroundColor = T.surface
  const cardBorderColor = T.surfaceBorder
  const titleTextColor = T.textDefault
  const subtitleTextColor = T.textDefault
  const statValueTextColor = T.isDark ? T.accent : T.textDefault
  //const statLabelTextColor = T.textDim 
  const statLabelTextColor = T.isDark ? T.textDefaultDim : T.textDefault
  const dotCorrectColor = T.success
  const dotIncorrectColor = T.danger

  const dotEmptyColor = T.textDefaultDim //unused, color can change if ever used

  const avgMs =
    status.totalElapsedMs != null && status.roundResults.length > 0
      ? Math.round(status.totalElapsedMs / status.roundResults.length)
      : null

  let titleText: string
  let showSubtitleText = false

  if (status.roundsCorrect === status.totalRounds) {
    titleText = 'Perfect run!'
  } else if (status.roundsCorrect > 0) {
    const roundText = status.roundsCorrect === 1 ? 'round' : 'rounds'
    titleText = `You got ${status.roundsCorrect} ${roundText} right!`
  } else {
    titleText = 'Better luck tomorrow.'
    showSubtitleText = true
  }

  const stats = [
    {
      value: status.roundsCorrect + '/' + status.totalRounds,
      label: 'Rounds'
    }
  ]
  if (status.totalElapsedMs != null) {
    stats.push({
      value: formatMs(status.totalElapsedMs),
      label: 'Total Time'
    })
  }
  if (avgMs != null) {
    stats.push({
      value: formatMs(avgMs),
      label: 'Avg/Round'
    })
  }

  return (
    <View style={[styles.card, { backgroundColor: cardBackgroundColor, borderColor: cardBorderColor }]}>
      {/*card row 1*/}
      <View style={styles.titleContainer}>
        <DS.Text style={[styles.titleContainerText, styles.titleText, { color: titleTextColor }]} variant="Heading 3/Medium">
          {titleText}
        </DS.Text>
        {showSubtitleText && (
          <DS.Text variant="Body Medium/Regular" style={{ color: subtitleTextColor }}>
            Keep practicing to improve your hand reading.
          </DS.Text>
        )}
      </View>

      {/*card row 2*/}
      <View style={styles.statsRow}>
        {stats.map(function (stat, i) {
          return (
            <View key={i} style={styles.statBox}>
              <DS.Text variant="Heading 2/Medium" style={{ color: statValueTextColor }}>
                {stat.value}
              </DS.Text>
              <DS.Text variant="Body Small/Regular" style={{ color: statLabelTextColor }}>
                {stat.label}
              </DS.Text>
            </View>
          )
        })}
      </View>

      {/*card row 3*/}
      <DS.Frame row justifyContent="center" gap="8">
        {status.roundResults.map((r: { correct: boolean }, i: number) => (
          <View key={i} style={[styles.dot, { backgroundColor: r.correct ? dotCorrectColor : dotIncorrectColor }]} />
        ))}
        {/*game play currently excludes empty rounds */}
        {/*Array.from({ length: status.totalRounds - status.roundResults.length }, (_, i) => (
          <View key={`e-${i}`} style={[styles.dot, {backgroundColor: dotEmptyColor}]} />
        ))*/}
      </DS.Frame>
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    //gap: 16
    gap: 20
  },
  titleContainer: {
    alignItems: 'center',
    gap: 4
  },
  titleContainerText: {
    textAlign: 'center'
  },
  titleText: {
    lineHeight: 30
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  statBox: {
    alignItems: 'center',
    gap: 4
  },
  dots: { flexDirection: 'row', justifyContent: 'center', gap: 8 },
  dot: { width: 12, height: 12, borderRadius: 6 },
  progressBar: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: 4
  },
})



export { CthStatsCard, D10StatsCard, PocketPickStatsCard }
export default StatsCard
